// Inventory namespace — RUSSIAN (Module 06 §4). The design partner is a cable
// factory in Almaty whose office staff AND floor operators work in Russian
// (docs/context/kazakhstan-group.md), so this file is not a nicety: an English
// floor PWA is an unusable floor PWA.
//
// It is typed `Translated<typeof inventoryEn>` (src/i18n-shape.ts), so a key
// added to or removed from `inventoryEn` fails `tsc` here. Values are checked
// separately, at runtime, by tools/ci/i18n-parity.ts.
//
// ── REGISTER ────────────────────────────────────────────────────────────────
// Warehouse/production Russian as a Kazakh cable plant actually speaks it, not
// calque-from-English. The load-bearing choices, so a reviewer can argue with
// them in one place rather than 950:
//
//  · movements   — приход / расход / перемещение / списание, and a reversal is
//                  «сторно» (the accounting word; «отмена» would suggest the
//                  entry disappears, and it does not — it is balanced).
//  · posting     — «провести / проведено» (провести в журнале), the standard
//                  verb for committing a ledger entry.
//  · reservation — «резерв». Russian has one plain word that means exactly
//                  this on a shop floor, and «блокировка» (the terminology base
//                  label for the `hold` TERM) is the quality hold — a different
//                  thing, so conflating them would be worse.
//                  (Amended 2026-08-20, task #112: this note used to add "the
//                  en copy says 'hold' to keep it plain for an operator". It
//                  did, and that was the defect — spec 07 §2.7 requires three
//                  DISTINCT words in BOTH locales for the three unrelated
//                  things called a hold here, and English used one word for
//                  two of them, one click apart. The en tab now reads
//                  "Reservations"; Russian is unchanged, because it was
//                  already right.)
//  · cutting     — отрезок (a cut piece), ширина реза (kerf), перемер
//                  (re-measure). These are the words used at the drum.
//  · counting    — пересчёт; «слепой пересчёт» for a blind count; расхождение
//                  for variance.
//
// ── ENTITY NOUNS ARE STILL NOT IN HERE ──────────────────────────────────────
// «Бобина», «Партия», «Место хранения» and friends are TERMINOLOGY keys (module
// 02 §2.5) resolved through `useTerm()` — TERMINOLOGY_BASE already ships the
// Russian for all 61 — and a tenant may override any of them. Where a string
// below interpolates one ({{unit}}, {{lot}}, {{offcut}}), the term arrives in
// the NOMINATIVE SINGULAR and this file cannot decline it. Every such sentence
// is therefore built so the nominative is grammatical: a colon, a dash, or
// parentheses, never a preposition that would demand a case.
//
// ── PLURALS ─────────────────────────────────────────────────────────────────
// Russian has three plural categories to en's two, and i18next expresses that
// with `_one/_few/_many` key SUFFIXES — extra keys, which the parity gate
// (rightly) rejects. So no counted noun is ever agreed with its number here.
// «{{count}} waiting to send» becomes «Ожидают отправки: {{count}}», which is
// correct for 1, 2, 5 and 21 alike. This is a deliberate constraint of the
// shared-key-set design, recorded in D-030.

import type { Translated } from "../../../i18n-shape.js";
import type { inventoryEn } from "./inventory.i18n.js";

export const inventoryRuDict: Translated<typeof inventoryEn> = {
  nav: {
    aria: "Разделы склада",
    lookup: "Поиск остатков",
    movements: "Движения",
    atp: "Доступность",
    uomConversions: "Пересчёт единиц",
    approvals: "Утверждения",
  },

  common: {
    loading: "Загрузка склада…",
    retry: "Повторить",
    requestId: "Идентификатор запроса",
    close: "Закрыть",
    cancel: "Отмена",
    confirm: "Подтвердить",
    remove: "Убрать",
    actions: "Действия",
    none: "Нет",
    noLocation: "Место не указано",
    remainingOfCapacity: "{{remaining}} из {{capacity}} {{uom}}",
    waitingOn: "Недоступно",
    waitingUnknown: "Недоступно",
    resolving: "Загружается название…",
  },

  waiting: {
    "04": "Данные производственных заказов не учтены",
    "05": "Без скидки на обрезки",
    "07": "Нет результатов проверок",
    "09": "Клиенты не указаны",
    "10": "Входящие заказы поставщикам не учтены",
    "12": "Решение принимается здесь",
  },

  filters: {
    facility: "Площадка",
    allFacilities: "Все площадки",
    pagination: "Постраничная навигация",
    prev: "Назад",
    next: "Вперёд",
    button: "Фильтры ({{n}})",
    title: "Фильтры",
    done: "Готово",
    remove: "Убрать: {{label}}",
    clearAll: "Сбросить все",
  },

  money: {
    unitCost: "Себестоимость единицы",
    withheld: "Скрыто",
    withheldHint: "Стоимость записана, но ваша роль не видит денежные суммы в этой записи.",
    unvalued: "Стоимость не записана",
    columnWithheld: "Стоимость на этой странице скрыта для вашей роли.",
    columnUnvalued: "Ни для одной записи на этой странице стоимость не записана.",
  },

  errors: {
    generic: "Запрос выполнить не удалось. Попробуйте ещё раз.",
    MOVEMENT_ILLEGAL: "Такое движение между местами хранения этих типов не допускается.",
    INSUFFICIENT_REMAINING: "На этой единице осталось меньше, чем требуется.",
    INSUFFICIENT_ATP: "Доступного остатка не хватает на это количество.",
    UOM_NOT_CONVERTIBLE: "Пересчёта между этой единицей и базовой единицей продукта нет.",
    NEGATIVE_STOCK_BLOCKED: "Остаток ушёл бы в минус, а это место хранения так не работает.",
    LOCATION_HIERARCHY_INVALID: "С таким родителем дерево мест хранения станет некорректным.",
    VIRTUAL_LOCATION_IMMUTABLE: "Системные места хранения изменять нельзя.",
    LOCATION_CODE_TAKEN: "Этот код уже занят другим местом хранения на этой площадке.",
    LOCATION_NOT_COUNTABLE: "Это место хранения пересчитывать нельзя.",
    ALLOW_NEGATIVE_PRODUCTION_ONLY:
      "Отрицательный остаток допустим только в производственных местах хранения.",
    MEASURE_PRECISION_LOSS: "Это количество нельзя пересчитать без потери точности.",
    RESERVATION_CONFLICT: "На этот остаток уже стоит действующий резерв.",
    RESERVATION_INVALID: "Такой тип потребности или такое количество зарезервировать нельзя.",
    LOT_NOT_RELEASED: "Эта партия ещё не прошла приёмку по качеству.",
    LOT_STATE_INVALID: "Из текущего состояния партии такой переход по качеству недопустим.",
    TRANSFER_STATE_INVALID:
      "Перемещение больше не в том состоянии, в котором это действие возможно.",
    COUNT_STATE_INVALID: "Пересчёт больше не в том состоянии, в котором это действие возможно.",
    MOVEMENT_ALREADY_REVERSED: "Это движение уже сторнировано.",
    REVERSAL_NOT_REVERSIBLE: "Сторно нельзя сторнировать.",
    ADJUSTMENT_APPROVAL_REQUIRED: "Эту корректировку нужно утвердить, прежде чем проводить.",
    ADJUSTMENT_REQUEST_STALE_STOCK: "После создания заявки остаток изменился.",
    UNIT_NOT_AVAILABLE: "Текущее состояние этой единицы не допускает такого действия.",
    CUT_NO_PIECES: "Укажите хотя бы один отрезок.",
    CUT_PIECE_NOT_POSITIVE: "Длина каждого отрезка должна быть больше нуля.",
    CUT_SOURCE_NEGATIVE: "На исходной единице резать больше нечего.",
    CUT_SERIAL_TAKEN: "Такой серийный номер уже используется.",
    CUT_TOLERANCE_INVALID: "Заданный допуск на рез некорректен.",
    CUT_FAULT_OUT_OF_RANGE: "Место дефекта находится за пределами отрезка.",
    RESOURCE_BUSY: "Прямо сейчас это меняет кто-то другой. Повторите через мгновение.",
    IDEMPOTENCY_KEY_REUSE:
      "Это уже отправлялось с другими данными. Откройте запись и сделайте заново.",
    IDEMPOTENCY_IN_PROGRESS: "Система ещё дорабатывает это. Повтор произойдёт сам.",
    IDEMPOTENCY_KEY_REQUIRED: "Система отклонила запрос. Сделайте это ещё раз.",
    TRANSFER_VARIANCE_REASON_REQUIRED: "Для строки с недостачей нужна причина.",
    CUT_EXCEEDS_REMAINING: "Этот рез длиннее того, что осталось на единице.",
    PERMISSION_DENIED: "У вас нет прав на это действие.",
    LOT_NOT_FOUND: "Партия не найдена.",
    LOCATION_NOT_FOUND:
      "Место хранения не найдено или относится к площадке, которая вам недоступна.",
    PRODUCT_NOT_FOUND: "Продукт не найден.",
    WORK_ORDER_NOT_FOUND:
      "Заказ на производство для этой корректировки не найден. Снимите отметку, чтобы записать её отдельно, или откройте форму из заказа.",
    ADJUSTMENT_REQUEST_STATE_INVALID: "По этому запросу уже принято решение, или его отозвали.",
    ADJUSTMENT_REQUEST_NOT_FOUND:
      "Такого запроса нет, или он относится к площадке, которая вам недоступна.",
    QR_LABEL_TEMPLATE_NOT_FOUND:
      "В вашем отраслевом пакете нет макета этикетки для этого. Администратор группы может добавить его обновлением отраслевого пакета в настройках.",
    PACK_NOT_INSTALLED: "Этикетки берутся из отраслевого пакета, а он ещё не установлен.",
    LABEL_PRINTER_UNAVAILABLE: "Печать этикеток в этой системе не настроена.",
    QR_PAYLOAD_MISSING: "У этих позиций пока нет кода для сканирования.",
    QR_KIT_SELECTION_EMPTY: "Выберите хотя бы одну позицию для печати этикетки.",
  },

  vocab: {
    status: {
      empty: "Пустая",
      filled: "Заполнена",
      in_use: "В работе",
      in_transit: "В пути",
      quarantined: "В карантине",
      consumed: "Израсходована",
      shipped: "Отгружена",
      retired: "Выведена из оборота",
      active: "Действует",
      fulfilled: "Исполнен",
      released: "Разблокирован",
      expired: "Истёк",
    },
    adjustmentRequest: {
      pending_approval: "Ждёт утверждения",
      posted: "Утверждено и проведено",
      rejected: "Отклонено",
      cancelled: "Отменено",
    },
    qc: {
      pending: "Ждёт контроля качества",
      released: "Допущена",
      quarantined: "В карантине",
      rejected: "Забракована",
    },
    transfer: {
      draft: "Черновик",
      dispatched: "Отправлено",
      partially_received: "Принято частично",
      received: "Принято",
      cancelled: "Отменено",
    },
    count: {
      draft: "Черновик",
      in_progress: "Идёт пересчёт",
      review: "На проверке",
      pending_approval: "Ждёт утверждения",
      posted: "Проведён",
      cancelled: "Отменён",
    },
    line: {
      pending: "Не пересчитана",
      counted: "Пересчитана",
      recount_requested: "Пересчитать заново",
      accepted: "Принята",
      rejected: "Отклонена",
    },
    movement: {
      receipt: "Приход",
      production_output: "Выпуск продукции",
      issue: "Расход",
      transfer_out: "Перемещение (отправка)",
      transfer_in: "Перемещение (приём)",
      adjustment: "Корректировка",
      count_adjustment: "Корректировка по пересчёту",
      cut_out: "Резка (списание)",
      cut_in: "Резка (оприходование)",
      scrap: "Списание в брак",
      return_to_supplier: "Возврат поставщику",
      shipment: "Отгрузка",
      repack: "Внутреннее перемещение",
      reversal: "Сторно",
      // Пятнадцатый тип (D-076/D-081, 04-B-4): возврат неизрасходованного
      // материала заказа на склад — собственное физическое событие, не сторно.
      material_return: "Возврат материала",
    },
    sourceDoc: {
      purchase_order: "Заказ на закупку",
      work_order: "Заказ на производство",
      sales_shipment: "Отгрузка",
      transfer: "Перемещение",
      cycle_count: "Циклический пересчёт",
      cut: "Резка",
      waste_ticket: "Акт на отходы",
      manual_adjustment: "Ручная корректировка",
      opening_balance: "Начальный остаток",
    },
    reason: {
      damage: "Повреждение",
      count_variance: "Расхождение при пересчёте",
      data_correction: "Исправление данных",
      loss: "Утеря",
      found: "Обнаружен излишек",
      qc_fail: "Несоответствие качеству",
      shrinkage: "Естественная убыль",
      remeasure: "Перемер",
      cut_tolerance: "Допуск на рез",
    },
    scan: {
      lookup: "Просмотрено",
      locate: "Размещено",
      count: "Пересчитано",
      receive: "Принято",
      issue: "Выдано",
      cut: "Отрезано",
      transfer_dispatch: "Отправлено",
      transfer_receive: "Принято",
      remeasure: "Перемерено",
    },
    relation: {
      consumed_into: "израсходовано в",
      cut_from: "отрезано от",
      reprocessed_into: "переработано в",
      repacked_into: "переупаковано в",
    },
    origin: {
      purchased: "Закуплено",
      produced: "Произведено",
      reprocessed: "Переработано",
      split: "Разделено",
      opening_balance: "Начальный остаток",
      adjustment: "Корректировка",
    },
    unitKind: {
      bobbin: "Бобина",
      drum: "Катушка",
      barrel: "Барабан",
      coil: "Бухта",
      roll: "Рулон",
      container: "Контейнер",
      pallet: "Паллета",
      box: "Коробка",
      piece: "Штука",
    },
    locationKind: {
      warehouse: "Склад",
      zone: "Зона",
      bin: "Ячейка",
      receiving: "Зона приёмки",
      shipping: "Зона отгрузки",
      in_transit: "В пути",
      quarantine: "Карантин",
      scrap: "Брак",
      production: "Производство",
      customer: "Клиент",
      supplier: "Поставщик",
    },
  },

  // §4.1 ---------------------------------------------------------------------
  lookup: {
    adjust: "Корректировка остатков",
    adjustRow: "Скорректировать",
    title: "Поиск остатков",
    searchLabel: "Искать по остаткам",
    placeholder: "например, 70mm 200m или заводской номер",
    hotkeyHint: "Нажмите / для поиска",
    includeReserved: "Включая зарезервированное",
    idleTitle: "Найдите нужный остаток",
    idleBody:
      "Введите сечение и длину — например, 70mm 200m — чтобы увидеть всё подходящее, либо заводской номер с барабана, который у вас в руках.",
    loading: "Идёт поиск…",
    errorTitle: "Не удалось выполнить поиск",
    emptyTitle: "По запросу «{{query}}» ничего нет",
    emptyBody: "Попробуйте другое сечение, длину, площадку — или заводской номер с этикетки.",
    emptyChecking: "Ищем близкие варианты…",
    emptyNearest: "Ближайшее — {{product}}, короче {{measure}} {{uom}}, позиций: {{n}}.",
    emptyNoNearest: "Ничего короче предложить тоже нельзя.",
    emptyReservedTitle: "Нет свободных остатков по запросу \u00ab{{query}}\u00bb",
    emptyReservedBody:
      "Все совпадения зарезервированы под заказы. Всего: {{count}}. Показать \u2014 увидите, под какие.",
    showReserved: "Показать зарезервированное",
    showAnyway: "Всё равно показать",
    keyboardHint:
      "↑ ↓ — переход по результатам · Enter — открыть · Shift+Enter — зарезервировать · Esc — очистить",
    groupCapped:
      "Группа показана не полностью — совпадений больше. Уточните запрос, чтобы увидеть остальные.",
    candidatesCapped:
      "Обрезков больше, чем удалось сравнить, поэтому число подходящих ниже — неполное.",
    productsCapped:
      "Под запрос подходит больше продуктов, чем можно просмотреть за раз, — часть остатков не показана. Добавьте артикул или название.",
    reservedFor: "В резерве · {{demand}} {{id}}",
    reserveBoth: "Зарезервировать все отрезки ({{n}})",
    resolving: "Загружаются данные отрезка…",
    hideAlternatives: "Скрыть альтернативы",
    groups: {
      units: "{{unit}}",
      combos: "Комбинации ({{offcut}})",
      bulk: "Навалом — {{lot}}",
      nearest: "Короче {{measure}} {{uom}}",
    },
    term: {
      offcut_discount: "Скидка на обрезки",
    },
    degradedIntro: "Часть данных отсутствует:",
    reserved: "В резерве",
    reserve: "Зарезервировать",
    cut: "Отрезать",
    open: "Открыть",
    neverScanned: "Ни разу не сканировалась",
    lastSeenMinutes: "Замечена {{n}} мин назад",
    lastSeenHours: "Замечена {{n}} ч назад",
    lastSeenDays: "Замечена {{n}} дн. назад",
    noCombos: "Ни одна пара обрезков не покрывает эту длину.",
    noCombosCapped:
      "Обрезков слишком много, чтобы сравнить все, и пара не найдена. Сузьте поиск по площадке или проверьте куски напрямую.",
    comboTotal: "Вместе {{total}} · остаётся лишних {{surplus}}",
    discountLabel: "Скидка:",
    rejected: {
      status_unavailable: "Рядом есть недоступные отрезки: {{n}}",
      lot_not_released: "Рядом есть отрезки, ожидающие допуска по качеству: {{n}}",
      reserved: "Рядом есть отрезки в резерве: {{n}}",
      other_facility: "Рядом есть отрезки на другой площадке: {{n}}",
      non_positive_remaining: "Рядом есть пустые отрезки: {{n}}",
    },
    colLot: "Партия",
    colProduct: "Продукт",
    colOnHand: "В наличии",
    colLocation: "Место хранения",
  },

  // §4.2 ---------------------------------------------------------------------
  unitCard: {
    loading: "Загрузка позиции…",
    errorTitle: "Не удалось загрузить эту позицию",
    notFoundTitle: "Позиция не найдена",
    notFoundBody: "Эта этикетка здесь не известна. Проверьте код или заведите позицию при приёмке.",
    product: "Продукт",
    bareContainer: "Пустая тара (без продукта)",
    openLot: "Открыть партию",
    claimedVsMeasured: "Заявлено и измерено",
    claimedMeasured: "заявлено {{claimed}} · измерено {{measured}} {{uom}}",
    driftBadge: "Отклонение {{pct}}",
    driftOwner: "Открыть калибровку",
    releaseHold: "Снять резерв",
    // NO PREPOSITION BEFORE AN INTERPOLATED TERM (ru-translation-notes §3).
    // `{{demand}}` is a NOMINATIVE noun phrase from inventory.reserve.kind.*
    // («Строка заказа на продажу»), so «по {{demand}}» rendered «по Строка
    // заказа на продажу» — `по` governs the dative and the phrase cannot inflect
    // to meet it. The dash form reads as a label and stays correct for every
    // member of the enum. The parity gate cannot see this class: the placeholder
    // multisets match either way.
    releaseHoldConfirm: "Снять резерв — {{demand}} {{id}}? Остаток вернётся в доступные.",
    holdReleased: "Резерв снят.",
    releaseHoldFailed: "Не удалось снять резерв.",
    reservationsNoPermission: "Ваша роль не видит резервы по этой позиции.",
    childrenTruncated:
      "У позиции больше отрезанных дочерних, чем помещается на страницу, — часть может отсутствовать.",
    lotEdges: "Связи на уровне партии",
    edgeDownstream: "вниз по цепочке",
    edgeUpstream: "вверх по цепочке",
    noClaim: "Заявленное значение не записано",
    noCapacity: "Вместимость не записана",
    ofCapacity: "из вместимости {{capacity}} {{uom}}",
    lastScan: "Последнее сканирование",
    lastScanValue: "{{action}} · {{when}}",
    activeHold: "Действующий резерв",
    // Same rule as releaseHoldConfirm above: «под {{demand}}» rendered «под
    // Строка заказа на продажу» — `под` governs the accusative/instrumental.
    heldFor: "{{quantity}} {{uom}} — в резерве: {{demand}}",
    actions: "Действия с позицией",
    actionCut: "Отрезать",
    actionRemeasure: "Перемерить",
    actionMove: "Переместить",
    actionReserve: "Зарезервировать",
    actionQuarantine: "В карантин",
    actionRelease: "Из карантина",
    actionRetire: "Вывести из оборота",
    blocked: {
      terminal: "Статус позиции: {{status}}. С ней больше ничего сделать нельзя.",
      inTransit: "Позиция в пути по перемещению. Сначала примите её.",
      empty: "Позиция пуста.",
      quarantined: "Сначала снимите карантин.",
      notHeld: "Позиция не на карантине.",
    },
    tabsAria: "Сведения о позиции",
    tabs: {
      history: "История",
      genealogy: "Генеалогия",
      reservations: "Резервы",
    },
    historyLoading: "Загрузка истории…",
    historyError: "Не удалось загрузить историю",
    historyEmptyTitle: "Истории пока нет",
    historyEmptyBody: "Здесь появятся движения и сканирования этой позиции.",
    colWhen: "Когда",
    colEvent: "Событие",
    colQuantity: "Количество",
    colNote: "Примечание",
    colDemand: "Потребность",
    colStatus: "Статус",
    colCreated: "Создано",
    scanBy: "Сканировал: {{who}}",
    genealogyLoading: "Загрузка генеалогии…",
    genealogyError: "Не удалось загрузить генеалогию",
    genealogyNoPermission: "У вашей роли нет доступа к прослеживанию генеалогии.",
    genealogyNoLotTitle: "Партии нет",
    genealogyNoLotBody: "Генеалогия ведётся по партиям; эта позиция пока ни к одной не относится.",
    genealogyEmptyTitle: "Связанных позиций нет",
    genealogyEmptyBody: "Ни одна позиция не записана как родительская или дочерняя для этой.",
    parents: "Изготовлено из",
    children: "Использовано в",
    noneRecorded: "Ничего не записано",
    openLotTrace: "Открыть полное прослеживание партии",
    reservationsLoading: "Загрузка резервов…",
    reservationsError: "Не удалось загрузить резервы",
    reservationsEmptyTitle: "Резервов нет",
    reservationsEmptyBody: "На эту позицию ничего не зарезервировано.",
    reservationsBareTitle: "Продукта нет",
    reservationsBareBody: "Пустую тару зарезервировать нельзя.",
    reservationsTruncated: "Проверены только самые свежие резервы по этому продукту.",
  },

  // Общий выбор места хранения (components/location-picker.tsx).
  locationPicker: {
    placeholder: "Введите код, название или путь…",
    typeToSearch: "Начните вводить, чтобы найти место хранения.",
    searching: "Ищем…",
    noMatch: "По запросу «{{q}}» мест хранения не найдено.",
    more: "Совпадений больше {{n}} — уточните запрос.",
    change: "Изменить",
    resolving: "Загрузка…",
    failed: "Не удалось выполнить поиск мест хранения. Попробуйте ещё раз.",
  },

  // §4.3 ---------------------------------------------------------------------
  locations: {
    childCount: "Внутри: {{count}}",
    collapseAria: "Скрыть содержимое {{code}}",
    expandAria: "Показать содержимое {{code}} ({{count}})",
    adjustHere: "Скорректировать остатки здесь",
    adjustRow: "Скорректировать",
    loading: "Загрузка мест хранения…",
    errorTitle: "Не удалось загрузить места хранения",
    emptyTitle: "Мест хранения пока нет",
    emptyBody:
      "Запустите комплект печати из мастера настройки или добавьте склад, чтобы начать дерево.",
    treeAria: "Дерево мест хранения",
    matchesAria: "Найденные места хранения",
    searchLabel: "Поиск мест хранения",
    searchPlaceholder: "Код, название или путь — например WH1/A",
    matches: "Найдено мест хранения: {{count}}.",
    matches_one: "Найдено место хранения: {{count}}.",
    matchesTruncated: "Найдено более {{n}} мест хранения — уточните запрос.",
    noMatchesTitle: "По запросу «{{q}}» ничего не найдено",
    noMatchesBody: "Поиск идёт по коду, названию и пути места хранения в дереве.",
    clearSearch: "Очистить поиск",
    noPhysical: "Складов, зон и ячеек пока нет.",
    virtualGroup: "Системные места хранения ({{n}})",
    virtualReadOnly: "Системные места хранения создаёт и ведёт сама платформа.",
    selectPrompt: "Выберите место хранения, чтобы увидеть его свойства и содержимое.",
    pickAria: "Выбрать {{code}} для печати этикеток",
    rename: "Переименовать",
    renamed: "Переименовано.",
    truncated: "Показано мест хранения: {{n}} — есть ещё.",
    showingAll: "Показаны все места хранения: {{n}}.",
    loadMore: "Загрузить ещё места хранения",
    loadingMore: "Загружаем ещё…",
    orphans:
      "Мест хранения, чьего родителя нет на этой странице: {{n}}. Показаны плоским списком, а не как корневые.",
    moveConfirm: "Переместить {{source}} вместе с вложенными ({{count}}) внутрь {{target}}?",
    moveConfirm_one: "Переместить {{source}} вместе с вложенным ({{count}}) внутрь {{target}}?",
    moveConfirmOffPage: "Переместить {{source}} вместе со всем, что внутри, внутрь {{target}}?",
    moved: "{{source}} перемещено внутрь {{target}}.",
    inactive: "Не действует",
    kind: "Тип",
    path: "Путь",
    countable: "Пересчитывается",
    countableHint: "Включать это место хранения в циклические пересчёты",
    active: "Действует",
    activeHint: "Доступно для новых движений",
    allowNegative: "Разрешить минус",
    allowNegativeHint: "Разрешить остатку уходить ниже нуля в течение смены",
    qrPayload: "Содержимое QR-кода",
    contents: "Что здесь лежит",
    contentsLoading: "Загрузка содержимого…",
    contentsError: "Не удалось загрузить содержимое",
    contentsEmptyTitle: "Здесь ничего не хранится",
    contentsEmptyBody: "Сейчас в этом месте хранения остатков нет.",
    colProduct: "Продукт",
    colOnHand: "В наличии",
    printQr: "Печать QR-этикеток ({{n}})",
    printQrBusy: "Формируется…",
    qrPickFirst: "Отметьте места хранения, для которых нужны наклейки.",
    qrPrinted: "Этикеток сформировано: {{n}}. PDF скачивается.",
    qrNoLink:
      "Лист сформирован, но ссылку на скачивание сейчас выдать не удалось. Откройте его из списка комплектов QR.",
    add: "Добавить место хранения",
    added: "Место хранения создано.",
    addSubmit: "Создать место хранения",
    addFailed: "Не удалось создать это место хранения.",
    code: "Код",
    name: "Название",
    parent: "Внутри",
    pickParent: "Выберите родителя…",
    pickFacility: "Выберите площадку…",
    parentRequired: "Перед созданием выберите, где находится это место хранения.",
    noBinParent: "На этой площадке ещё нет склада или зоны. Сначала создайте склад.",
    noZoneParent: "На этой площадке ещё нет склада. Сначала создайте склад.",
    reparentFailed: "Не удалось переместить это место хранения. Попробуйте ещё раз.",
    updateFailed: "Не удалось сохранить изменение. Попробуйте ещё раз.",
  },

  // §4.4 ---------------------------------------------------------------------
  transfers: {
    status: "Статус",
    allStatuses: "Все статусы",
    from: "Откуда",
    to: "Куда",
    loading: "Загрузка перемещений…",
    errorTitle: "Не удалось загрузить перемещения",
    emptyTitle: "Перемещений нет",
    emptyBody: "Под эти фильтры ничего не подходит.",
    colCode: "Код",
    colRoute: "Маршрут",
    otherPlant: "Другая площадка",
    colStatus: "Статус",
    colEta: "Ожидается",
    colAge: "В пути",
    noEta: "Срок не указан",
    onTime: "В срок",
    daysLate: "Просрочка {{n}} дн.",
    new: "Новое перемещение",
    emptyFilteredTitle: "Подходящих перемещений нет",
    emptyFilteredBody: "Под эти фильтры ничего не подходит.",
    pickFacility: "Выберите площадку",
    addLine: "Добавить строку",
    lineSerial: "Серийный номер позиции (необязательно)",
    lineProduct: "Продукт (строка навалом)",
    linePickProduct: "Поиск по коду или названию",
    lineProductFromUnit: "Взято из отсканированной позиции.",
    lineLot: "Партия",
    lineLotAny: "Без партии (непартионный остаток)",
    lineLotTruncated: "Показаны {{n}} самых свежих партий. Более старые здесь не перечислены.",
    lineUom: "Единица",
    needFacilities: "Укажите, откуда груз уходит и куда идёт.",
    needDistinctFacilities: "Перемещение идёт между двумя разными площадками.",
    needLine: "Добавьте хотя бы одну строку с количеством и единицей.",
    needLineSubject: "В каждой строке нужен серийный номер позиции или продукт.",
    createSubmit: "Создать черновик",
    creating: "Создаётся…",
    createFailed: "Не удалось создать это перемещение.",
    created: "Черновик {{code}} создан.",
    openCreated: "Открыть черновик",
  },
  transferDetail: {
    cancelDone: "Перемещение отменено.",
    noLinesTitle: "Строк пока нет",
    noLinesBody: "В черновике ничего нет — добавьте строки, прежде чем отправлять.",
    loading: "Загрузка перемещения…",
    errorTitle: "Не удалось загрузить это перемещение",
    timeline: "Ход перемещения",
    created: "Создано",
    dispatched: "Отправлено",
    received: "Принято",
    cancelled: "Отменено",
    pending: "Ещё нет",
    by: "исполнил {{who}}",
    carrier: "Перевозчик",
    cancelReason: "Причина",
    actions: "Действия с перемещением",
    dispatch: "Отправить",
    dispatching: "Отправляется…",
    dispatchFailed: "Не удалось отправить это перемещение.",
    cancel: "Отменить перемещение",
    confirmCancel: "Отменить это перемещение",
    cancelFailed: "Не удалось отменить это перемещение.",
    lines: "Строки",
    colProduct: "Продукт",
    colQty: "Отправлено",
    colReceived: "Принято",
    colVariance: "Причина расхождения",
    receiveOnFloor:
      "Приёмка идёт в цеховом приложении, где каждую позицию сканируют при поступлении.",
    activity: "Записи журнала",
    activityLoading: "Загрузка записей…",
    activityError: "Не удалось загрузить записи",
    activityEmpty: "По этому перемещению записей в журнале пока нет.",
  },

  // §4.5 ---------------------------------------------------------------------
  counts: {
    status: "Статус",
    allStatuses: "Все статусы",
    loading: "Загрузка пересчётов…",
    errorTitle: "Не удалось загрузить пересчёты",
    emptyTitle: "Проверять нечего",
    emptyBody: "Под эти фильтры пересчётов нет.",
    emptyListTitle: "Пересчётов нет",
    emptyPerformBody: "Начните новый, чтобы пересчитать место хранения.",
    noReviewGrant: "Ваша роль может смотреть пересчёты, но не проверять и не утверждать их.",
    performOnlyGrant:
      "Ваша роль может начинать и выполнять пересчёты; утверждение — задача проверяющего.",
    colCode: "Пересчёт",
    colStatus: "Статус",
    colBlind: "Режим",
    colSubmitted: "Сдан",
    colVarianceValue: "Стоимость расхождений",
    blind: "Слепой",
    open: "Открытый",
    new: "Новый пересчёт",
    noCountable: "На этой площадке пока нет мест хранения, подлежащих пересчёту.",
    blindToggle: "Слепой пересчёт",
    blindHint: "При слепом пересчёте ожидаемые количества скрыты от счётчика до самой проверки.",
    createSubmit: "Создать пересчёт",
    createFailed: "Не удалось создать этот пересчёт.",
    created: "Пересчёт {{code}} создан.",
    openCreated: "Открыть пересчёт",
    worksheetOnFloor: "Пересчёт ведут в цеховом приложении; на этом экране проверяют результат.",
  },
  countReview: {
    loading: "Загрузка пересчёта…",
    errorTitle: "Не удалось загрузить этот пересчёт",
    totals: "Итоги",
    totalsValue: "Строк: {{lines}} · на проверку: {{variances}}",
    expectedHidden: "Пока слепой пересчёт идёт, ожидаемые количества скрыты.",
    emptyTitle: "Расхождений нет",
    emptyBody: "Пересчёт сошёлся на 100%. Утвердите, чтобы провести и закрыть его.",
    colProduct: "Продукт",
    colExpected: "Ожидалось",
    colCounted: "Пересчитано",
    colDeltaQty: "Δ количество",
    colDeltaValue: "Δ стоимость",
    colStatus: "Статус",
    colActions: "Действия",
    notCounted: "Пропущено",
    accept: "Принять",
    reject: "Отклонить",
    recount: "Пересчитать заново",
    rejectNote: "Почему это отклонено?",
    confirmReject: "Отклонить строку",
    reviewFailed: "Не удалось записать это решение.",
    approve: "Утвердить и провести",
    approving: "Утверждается…",
    approveFailed: "Не удалось утвердить этот пересчёт.",
    posted: "Утверждено — корректировки проведены в журнале.",
    escalated: "Выше вашего лимита утверждения — отправлено на согласование.",
    movedLines:
      "Строк, изменившихся после снимка пересчёта: {{n}}. Они возвращены на повторный пересчёт. Ничего не проведено.",
    notPosted: "Не проведено.",
    localTier: "Решение принято здесь.",
    chain: "Цепочка согласования {{id}}.",
    noLinesTitle: "В этом пересчёте нет строк",
    noLinesBody:
      "В охват ничего не попало — отмените его или начните новый пересчёт по месту хранения, где есть остатки.",
    blindPendingTitle: "Расхождения пока показать нельзя",
    blindPendingBody:
      "Слепой пересчёт ещё идёт, а ожидаемые количества скрыты от вашей роли, поэтому расхождение здесь рассчитать невозможно.",
    colCountedBy: "Пересчитал",
    colInvestigate: "После пересчёта",
    openLedger: "Движения",
    recountOf: "Повторный пересчёт",
    approveConfirm:
      "Провести корректировки в журнале ({{n}})? Записи постоянные, и отменить их можно только сторнированием каждой.",
    approveConfirmSubmit: "Да, провести",
    approveBlindValue:
      "Стоимость этих корректировок вам не видна — вы утверждаете только количества.",
    approveUnvalued:
      "Для этого остатка стоимость не записана, поэтому корректировки пройдут без суммы.",
    pendingApprovalNote:
      "Этот пересчёт выше вашего лимита утверждения и ждёт решения вышестоящего уровня.",
    openApprovals: "Открыть утверждения",
    cancelCount: "Отменить этот пересчёт",
    cancelReason: "Почему он отменяется?",
    cancelSubmit: "Отменить пересчёт",
    cancelFailed: "Не удалось отменить этот пересчёт.",
  },

  // §4.6 ---------------------------------------------------------------------
  lotDetail: {
    adjust: "Корректировка остатков",
    adjustRow: "Скорректировать",
    loading: "Загрузка партии…",
    errorTitle: "Не удалось загрузить эту партию",
    product: "Продукт",
    origin: "Происхождение",
    supplier: "Поставщик {{id}}",
    received: "Принята",
    expires: "Годна до",
    actions: "Действия с партией",
    quarantine: "В карантин",
    release: "Допустить",
    quarantineReason: "Почему эта партия задерживается?",
    releaseNote: "Примечание к допуску",
    qcFailed: "Не удалось изменить статус качества.",
    qcDone: "Статус качества обновлён.",
    traceUp: "Проследить ↑ вверх",
    traceDown: "Проследить ↓ вниз",
    attributes: "Атрибуты",
    noAttributes: "Для этой партии атрибуты не записаны.",
    stockSummary: "Где она находится",
    noStockTitle: "Остатков нет",
    noStockBody: "Эта партия полностью израсходована, отгружена или списана.",
    colOnHand: "В наличии",
    noUnits: "Эта партия учитывается навалом — отдельных позиций нет.",
    unitsTruncated: "Показано {{shown}} из {{total}} позиций.",
    colSerial: "Серийный №",
    colStatus: "Статус",
    colRemaining: "Остаток",
  },

  // §4.7 ---------------------------------------------------------------------
  trace: {
    title: "Прослеживание для отзыва",
    loading: "Идёт прослеживание…",
    errorTitle: "Не удалось выполнить прослеживание",
    noPermissionTitle: "Прослеживание недоступно",
    noPermissionBody: "У вашей роли нет доступа к прослеживанию генеалогии.",
    direction: "Направление прослеживания",
    up: "↑ Вверх по цепочке",
    down: "↓ Вниз по цепочке",
    exportPack: "Выгрузить пакет для отзыва",
    truncated:
      "Прослеживание упёрлось в предел глубины — более глубокие связи есть, но не показаны.",
    truncatedPlain:
      "Прослеживание упёрлось в предел глубины; более глубокие связи есть, но не включены.",
    clientTruncated: "Показ остановлен после {{n}} строк — граф больше того, что здесь видно.",
    clientTruncatedPlain:
      "Показ остановлен на предельном числе строк; граф больше того, что сюда вошло.",
    depth: "глубина {{n}}",
    tileFinishedLots: "Готовые партии",
    tileStockUnits: "Позиции",
    tileShipments: "Отгрузки",
    tileCustomers: "Клиенты",
    tileRawLots: "Сырьевые партии",
    tileSuppliers: "Поставщики",
    customersUnavailable: "Клиентов здесь назвать нельзя. Отгрузки перечислены полностью.",
    customersUnavailablePlain: "Затронутых клиентов назвать не удалось. Строки отгрузок полные.",
    customersPartial:
      "По {{unresolved}} из {{total}} отгрузок нет документа отгрузки в продажах, поэтому их клиенты неизвестны. Считайте этот список клиентов неполным.",
    customersPartialPlain:
      "Список клиентов неполный: по {{unresolved}} из {{total}} отгрузок нет документа отгрузки в продажах, их клиенты неизвестны. Строки отгрузок при этом полные.",
    tileIncomplete: "неполно",
    graph: "Цепочка",
    emptyTitle: "Связей нет",
    emptyDownBody: "Эту партию пока никуда не израсходовали и не отгрузили.",
    emptyUpBody: "Для этой партии не записаны ни родительские партии, ни позиции.",
    alreadyShown: "(показано выше)",
    boundary: "Где прослеживание остановилось",
    boundaryHint: "Эти узлы достигнуты на пределе — разворачивайте их с их собственных страниц.",
    shipments: "Отгрузки",
    noShipments: "Эту партию не затрагивает ни одна отгрузка.",
    colWhen: "Когда",
    colQty: "Количество",
    colRef: "Ссылка",
  },

  // §4.8 ---------------------------------------------------------------------
  movements: {
    sourceDocFilter: "Только движение по запросу {{id}}",
    title: "Движения",
    export: "Выгрузить CSV",
    exportPage: "Выгрузить эти строки ({{n}})",
    exportScopePlain: "Выгрузка {{n}} строк с экрана — не всей отфильтрованной выборки",
    exportPunctuationCommaDot: "разделитель — запятая, десятичная точка",
    exportPunctuationSemicolonComma: "разделитель — точка с запятой, десятичная запятая",
    serialPlaceholder: "Введите серийный номер позиции",
    clearFilter: "Очистить",
    allLocations: "Все места хранения",
    type: "Тип",
    allTypes: "Все типы",
    sourceDoc: "Документ-основание",
    allSourceDocs: "Все документы",
    product: "Продукт",
    productPlaceholder: "Поиск по продуктам…",
    idPlaceholder: "Вставьте идентификатор",
    performer: "Исполнитель",
    performerPlaceholder: "Идентификатор пользователя или агента",
    from: "С",
    to: "По",
    loading: "Загрузка движений…",
    errorTitle: "Не удалось загрузить движения",
    emptyTitle: "Движений нет",
    emptyBody: "Под эти фильтры ничего не подходит.",
    colWhen: "Когда",
    colType: "Тип",
    colProduct: "Продукт",
    colQuantity: "Количество",
    colSource: "Основание",
    colExpand: "Подробно",
    colFrom: "Откуда",
    colTo: "Куда",
    colQtyBase: "Количество в базовой единице",
    colPerformer: "Исполнитель",
    colReason: "Причина",
    colNote: "Примечание",
    colUnit: "Позиция",
    colLot: "Партия",
    expand: "Открыть",
    collapse: "Закрыть",
    reverse: "Сторнировать это движение",
    reverseWarning: "Сторно пишет новую постоянную запись в журнал. Отменить её нельзя.",
    reverseNote: "Примечание (необязательно)",
    reverseConfirm: "Да, сторнировать",
    reversing: "Сторнируется…",
    reversed: "Сторнировано. Записана уравновешивающая проводка.",
    reverseFailed: "Не удалось сторнировать это движение.",
  },

  // §4.9 ---------------------------------------------------------------------
  atp: {
    title: "Доступность",
    product: "Продукт",
    productPlaceholder: "Поиск по продуктам…",
    pickFacility: "Выберите площадку…",
    date: "На дату",
    idleTitle: "Выберите продукт",
    idleBody:
      "Выберите продукт и площадку, чтобы увидеть, из чего складывается доступное количество.",
    loading: "Расчёт доступности…",
    errorTitle: "Не удалось рассчитать доступность",
    asOf: "{{product}} · {{date}}",
    partialSum: "Часть составляющих отсутствует, поэтому итог учитывает только показанные ниже.",
    colTerm: "Составляющая",
    colValue: "Количество",
    colDrill: "Подробно",
    noDrill: "—",
    drillLedger: "Открыть журнал",
    drillReservedStock: "Открыть остатки по этому продукту",
    drillTransfers: "Открыть перемещения в пути",
    term: {
      on_hand: "В наличии",
      reserved: "В резерве",
      in_transit_inbound: "В пути, входящее",
      po_inbound: "Ожидается по закупкам",
      wo_planned_output: "Плановый выпуск",
      wo_demand: "Потребность производства",
    },
  },

  // §4.10 --------------------------------------------------------------------
  uom: {
    title: "Пересчёт единиц",
    ownedByCatalogue:
      "Коэффициенты пересчёта задаются у самого продукта, в каталоге. На этой странице видно, где коэффициента не хватает.",
    scanning: "Проверяются недавние движения…",
    window: "Проверено по {{n}} последним движениям.",
    errorTitle: "Не удалось проверить пересчёты",
    cleanTitle: "Недостающих пересчётов нет",
    cleanBody: "Единица каждого недавнего движения пересчитывается в базовую единицу продукта.",
    warning: "Продуктов с движениями в единице, не пересчитываемой в базовую: {{n}}.",
    colProduct: "Продукт",
    colBase: "Базовая единица",
    colMissing: "Нет пересчёта из",
    colFix: "Исправить",
    openConversions: "Открыть пересчёты",
    viewConversions: "Посмотреть пересчёты",
  },

  // §4.11 --------------------------------------------------------------------
  approvals: {
    newAdjustment: "Новая корректировка",
    openRequest: "Открыть",
    pageRange: "Показаны {{from}}–{{to}}",
    title: "Утверждение корректировок",
    localTierNote:
      "Сумма, которую вы можете утвердить, зависит от вашей роли и стоимости корректировки.",
    status: "Статус",
    allStatuses: "Все статусы",
    loading: "Загрузка заявок…",
    errorTitle: "Не удалось загрузить заявки",
    emptyPendingTitle: "На утверждении ничего нет",
    emptyPendingBody:
      "По всем заявкам решение принято. Корректировки дороже порогового значения попадают сюда на утверждение до проведения.",
    emptyTitle: "Заявок нет",
    emptyBody: "Под эти фильтры ничего не подходит.",
    colRequested: "Запрошено",
    colBy: "Кто запросил",
    colWhat: "Корректировка",
    colValue: "Ориентировочная стоимость",
    colStatus: "Статус",
    postedMovement: "Открыть проведённое движение",
    rejectReason: {
      stale_stock: "Отклонено автоматически — остаток изменился после создания заявки.",
    },
    payloadUnreadable: "Отложенное движение прочитать не удалось.",
    approveWarning:
      "Утвердить эту корректировку? Она сразу проводится в журнале, и отменить её можно только сторнированием движения.",
    rejectWarning:
      "Отклонить эту корректировку? Ничего не проводится, и заявителю придётся создать заявку заново.",
    cancelWarning: "Отменить эту заявку? Ничего не проводится, она закрывается как отменённая.",
    note: "Примечание (необязательно)",
    noteRequired: "Почему это отклоняется?",
    approve: "Утвердить",
    reject: "Отклонить",
    cancel: "Отменить заявку",
    readOnly: "Ваша роль видит эти заявки, но не принимает по ним решения.",
    failed: "Не удалось записать это решение.",
    approved: "Утверждено — проведено в журнале как движение {{id}}.",
    rejected: "Отклонено. Ничего не проведено.",
    cancelled: "Заявка отменена. Ничего не проведено.",
  },

  // Общие панели записи ------------------------------------------------------
  reserve: {
    title: "Зарезервировать остаток",
    quoteHoldStatement:
      "Резерв для клиента, который запросил остаток, до появления заказа. Снимается сам, если заказа не будет.",
    demandRef: "Под что резерв?",
    demandRefPlaceholder: "BuildCo · 200 м 70 мм² · обещали к четвергу",
    demandRefHint:
      "Клиент и что он просил. Повторите ту же формулировку, чтобы добавить остаток в этот резерв.",
    demandRefTooShort:
      "Напишите не меньше {{min}} символов. Пометку из одной-двух букв легко спутать с чужим резервом в тот же день.",
    quantity: "Количество ({{uom}})",
    allowBackorder: "Разрешить резерв, даже если он превышает доступный остаток",
    submit: "Поставить резерв",
    saving: "Резерв ставится…",
    failed: "Не удалось поставить резерв.",
    created: "В резерве {{quantity}} {{uom}}.",
    expiresAt: "Снимется автоматически {{when}}, если не станет заказом.",
    available: "Доступно сейчас: {{quantity}} {{uom}}.",
    heldFor: "Уже в резерве под: {{ref}}.",
    allCreated: "Все отрезки в резерве: {{n}}.",
    partial:
      "Зарезервировано {{ok}} отрезков из {{n}} — остальные не удалось. Смотрите каждый отрезок ниже.",
    multiHint: "По одному резерву на отрезок: отрезков {{n}}, всего {{total}} {{uom}}.",
    kind: {
      sales_order_line: "Строка заказа на продажу",
      work_order_component: "Компонент заказа на производство",
      quote_hold: "Резерв под коммерческое предложение",
      maintenance_order_part: "Запчасть по заявке на ремонт",
      transfer_line: "Строка перемещения",
    },
    otherKindsHead: "Другие виды резерва здесь не ставятся:",
    otherKinds: {
      work_order_component:
        "Производство ставит их само: при запуске заказа на производство резервируются все материалы по спецификации.",
      sales_order_line:
        "Остаток под заказ на продажу резервирует сам заказ, в разделе продаж. Форма выше резервирует остаток под клиента.",
      maintenance_order_part:
        "Запчасти для обслуживания здесь не резервируются. Резервируйте их под работу выше.",
    },
  },
  cut: {
    title: "Резка",
    sourceRemaining: "Осталось на этой позиции: {{remaining}} {{uom}}",
    colLength: "Длина ({{uom}})",
    colKind: "Отрезок",
    colSerial: "Серийный №",
    serialAuto: "Автоматически",
    kindChild: "Оставить",
    kindWaste: "Отход",
    addPiece: "Добавить отрезок",
    kerf: "Ширина реза ({{uom}})",
    kerfHint: "Материал, теряемый на каждом разрезе; считается один раз за рез.",
    qcFail: "Это рез по несоответствию качеству (отрезки уходят в карантин)",
    submit: "Отрезать",
    saving: "Идёт резка…",
    failed: "Не удалось отрезать от этой позиции.",
    freshRemaining: "Теперь осталось {{remaining}} {{uom}} — введите рез заново.",
    done: "Отрезано {{n}} шт. На исходной позиции осталось {{remaining}} {{uom}}.",
    offcut: "обрезок",
    waste: "Отходы: {{total}} {{uom}}",
    reservationNotCovered:
      "Существующий резерв не удалось перенести ни на один отрезок — разберитесь с ним.",
  },
  remeasure: {
    containerHint:
      "Здесь исправляют содержимое тары. Остатки россыпью исправляют корректировкой остатков.",
    title: "Перемер",
    current: "Сейчас записано: {{remaining}} {{uom}}",
    entered: "Измеренное значение ({{uom}})",
    submit: "Сохранить измерение",
    saving: "Сохраняется…",
    failed: "Не удалось сохранить измерение.",
    adjusted: "Скорректировано на {{delta}} {{uom}}.",
    withinTolerance: "В пределах допуска — ничего не корректировалось.",
  },
  move: {
    title: "Перемещение",
    moving: "Перемещается {{quantity}} {{uom}}",
    destination: "Переместить в",
    noOrigin: "У этой позиции ещё не отмечено место хранения — сначала отсканируйте её.",
    submit: "Переместить",
    saving: "Перемещается…",
    failed: "Не удалось переместить эту позицию.",
    done: "Перемещено.",
    openApprovals: "Открыть утверждения",
    heldForApproval: "Это перемещение выше порога утверждения и ждёт решения.",
  },
  quarantine: {
    title: "Карантин",
    reason: "Почему это задерживается?",
    relocate: "Также перевезти в зону карантина",
    relocateHint: "Не отмечайте, чтобы задержать на месте.",
    submit: "Отправить в карантин",
    failed: "Не удалось отправить эту позицию в карантин.",
    done: "В карантине.",
  },
  releaseUnit: {
    title: "Выпуск из карантина",
    note: "Примечание",
    returnTo: "Вернуть в",
    inPlace: "Оставить на месте",
    submit: "Выпустить",
    failed: "Не удалось выпустить эту позицию.",
    done: "Выпущено.",
  },
  retire: {
    title: "Вывод из оборота",
    hint: "Вывести из оборота можно только пустую или полностью израсходованную тару. Это пишет постоянную запись в журнал.",
    reasonCode: "Причина",
    confirm: "Да, вывести {{serial}} из оборота",
    submit: "Вывести из оборота",
    failed: "Не удалось вывести эту позицию из оборота.",
    done: "Выведено из оборота.",
  },

  // =========================================================================
  // ЦЕХОВОЕ ПРИЛОЖЕНИЕ (spec 06 §4.1/§4.2 floor, §4.4, §4.5, §9.2 — D-048).
  //
  // Правила текста те же, что в английском оригинале, и в русском они значат
  // ровно то же: короткая фраза, читаемая на вытянутой руке; никакого
  // инженерного жаргона (ни «409», ни «идемпотентность», ни «очередь исходящих»
  // — у оператора модель «ждёт отправки» / «требует внимания»); и каждая
  // фраза об ошибке говорит, ЧТО ДЕЛАТЬ ДАЛЬШЕ, потому что иначе устройство
  // просто кладут на стол.
  // =========================================================================
  // §4.12 Корректировка остатков (D-315) ----------------------------------
  adjust: {
    title: "Корректировка остатков",
    intro: "Оприходуйте или спишите россыпь; тара исправляется в своей карточке.",
    entry: "Корректировка остатков",
    entryRow: "Скорректировать",
    fromProduction: "Провести корректировку остатков",
    back: "Назад: {{place}}",
    workOrderClosed:
      "Относится к: {{name}}. Закрытый документ материал обратно не принимает, поэтому остаток оформляется здесь.",
    workOrderOpen: "Относится к: {{name}}.",
    recordAgainst: "Записать по заказу: {{name}}",
    recordAgainstHint:
      "Тогда заказ покажет её в разделе «Корректировки по этому заказу». Снимите отметку, если материал не относится к этому заказу.",
    directionLabel: "Направление",
    increase: "Оприходовать",
    decrease: "Списать",
    product: "Продукт",
    productPlaceholder: "Поиск по артикулу или названию",
    noLot: "Нет — продукт учитывается без партий",
    pickLot: "Выберите",
    lotRequired:
      "Продукт учитывается по партиям, поэтому выберите, к чему относится остаток: {{lot}}.",
    lotNone: "У продукта ещё нет записей ({{lot}}). Они создаются при приёмке.",
    location: "Где лежит",
    counterLabel: "Встречная сторона в учёте",
    counter: {
      found: "Не было в учёте (излишек)",
      wip: "Незавершённое производство ({{path}})",
      scrap: "Списание",
    },
    counterShort: {
      found: "не было в учёте (излишек)",
      wip: "незавершённое производство",
      scrap: "списание",
    },
    counterMissing:
      "На этой площадке нет места для такой встречной стороны. Обратитесь к администратору.",
    quantity: "Количество",
    unit: "Единица",
    unitBase: "{{code}} (базовая)",
    quantityInvalid: "Введите количество больше нуля, не больше 6 знаков после запятой.",
    reason: "Причина",
    reasonPick: "Выберите причину",
    note: "Что произошло — своими словами",
    noteShort: "Напишите не меньше 10 знаков. Сейчас {{n}}.",
    noteCount: "{{n}} из 2000",
    askApproval: "Отправить на утверждение, даже если сумма небольшая",
    approvalHint:
      "Если сумма выше лимита компании, корректировка не проводится сразу, а ждёт решения руководителя. Вы увидите, что произошло.",
    loose: "Россыпью здесь: {{qty}} {{uom}}",
    looseNone: "Здесь нет остатка россыпью, списывать нечего.",
    pinned: "Здесь есть и тара с этим остатком ({{n}}). Её исправляют в карточке тары.",
    pinnedOpen: "Открыть первую",
    wipHeld: "В незавершённом производстве: {{qty}} {{uom}}.",
    overLoose: "Это больше, чем {{qty}} {{uom}} россыпью здесь.",
    overWip: "В незавершённом производстве только {{qty}} {{uom}}, больше вернуть нельзя.",
    otherUnit:
      "Лимиты выше указаны в базовой единице. В другой единице количество проверяется при сохранении.",
    waitStock: "Ждём, пока загрузятся остатки.",
    incomplete: "Заполните все поля, чтобы провести корректировку.",
    submit: "Провести корректировку",
    saving: "Проводим…",
    failed: "Не удалось провести корректировку.",
    posted: "Проведено в журнале движений.",
    held: "Ждёт утверждения. В учёте пока ничего не изменилось.",
    seeLedger: "Открыть в журнале движений",
    openRequest: "Открыть запрос",
    another: "Провести ещё одну",
    loading: "Загружаем форму корректировки…",
    errorTitle: "Не удалось загрузить данные для формы",
    noPlantTitle: "Нет площадки, где вы можете корректировать остатки",
    noPlantBody:
      "Корректировки проводятся на площадке, к которой у вас есть доступ. Попросите администратора добавить вас.",
    forbiddenTitle: "Вы не можете корректировать остатки",
    forbidden: "Корректировки остатков проводят кладовщики и администраторы.",
    prefillMissing:
      "Часть данных из ссылки больше не существует или здесь не подходит, поле очищено.",
    offlineTitle: "Нет связи",
    offlineBody: "Без связи остатки не загрузить. Ничего не отправлено.",
    looseUnknown:
      "Здесь больше строк остатка, чем помещается на одну страницу, поэтому остаток россыпью не показан. Количество проверяется при сохранении.",
    wipOutput:
      "В незавершённом производстве {{qty}} {{uom}} — ниже нуля, так как заказ списал это как свой выпуск. Оприходование не ограничено.",
    fence: {
      quarantine_place:
        "Остаток на карантине нельзя передать в незавершённое производство. Спишите его или дождитесь решения ОТК.",
      lot_not_released:
        "ОТК ещё не выпустил эту запись ({{lot}}), поэтому её нельзя передать в незавершённое производство. Спишите её или сначала выпустите.",
    },
    transitHold:
      "Груз, потерянный в пути, списывается только с утверждения руководителя, какой бы ни была сумма.",
    container: {
      live: "В {{serial}} ещё есть остаток, поэтому снятое с неё возвращается на неё через «Перемер» в её карточке, а не россыпью здесь.",
      held: "{{serial}} удерживается ОТК. Остаток с неё ждёт решения ОТК.",
      no_stock:
        "В {{serial}} больше нет остатка, поэтому снятое с неё возвращается россыпью (запись: {{lot}}).",
    },
    containerOpen: "Открыть {{serial}}",
    looseConfirm: "Это материал россыпью, а не на таре",
    looseReason: "Материал, который ещё на таре, исправляют в карточке этой тары через «Перемер».",
    expectedOrder: "По заказу выдано и не возвращено {{qty}} {{uom}}. Введите то, что взвесили.",
    expectedFloor: "Цех отметил возврат {{qty}} {{uom}}. Введите то, что взвесили.",
    noteLeftoverClosed: "Остаток материала по документу {{name}}, возвращён после закрытия.",
    noteLeftover: "Остаток материала по документу {{name}}.",
    noteReceipt: "Исправление приёмки по документу {{name}}.",
    unknown:
      "Возможно, прошлое нажатие уже прошло. Проверьте, прежде чем что-то менять: «Провести корректировку» отправит ту же корректировку повторно и не проведёт её дважды.",
    unknownLedger: "Проверить журнал движений",
    unknownApprovals: "Проверить утверждения",
    startOver: "Начать заново",
  },

  adjustRequest: {
    title: "Запрос на корректировку",
    loading: "Загружаем запрос…",
    errorTitle: "Не удалось загрузить запрос",
    notFoundTitle: "Такого запроса нет, или он относится к площадке, которая вам недоступна",
    notFoundBody: "Проверьте ссылку или откройте список утверждений.",
    lotPart: ", {{lot}}: {{code}}",
    summary: {
      increase:
        "Оприходовать {{qty}} {{uom}}: {{product}}{{lotPart}}, место: {{place}}; встречная сторона: {{counter}}",
      decrease:
        "Списать {{qty}} {{uom}}: {{product}}{{lotPart}}, место: {{place}}; встречная сторона: {{counter}}",
      relocation: "Переместить {{qty}} {{uom}}: {{product}}{{lotPart}}, из {{from}} в {{to}}",
      other: "{{type}}: {{qty}} {{uom}}, {{product}}{{lotPart}}, из {{from}} в {{to}}",
    },
    reason: "Причина",
    note: "Своими словами",
    requested: "Запросил(а) {{who}}, {{when}}",
    status: {
      pending_approval: "Ждёт решения с {{when}}.",
      posted: "Утверждено: {{who}}, {{when}}. Проведено.",
      rejected: "Отклонено: {{who}}. Причина: {{reason}}",
      stale:
        "Отклонено: остатки изменились до решения. Проведите корректировку заново по текущим остаткам.",
      cancelled: "Отозвано: {{who}}, {{when}}.",
    },
    seeLedger: "Открыть движение в журнале",
    workOrder: "Относится к заказу",
    recordAgain: "Провести заново",
    value: "Оценочная стоимость",
    stateChanged:
      "Только что кто-то принял решение по запросу или отозвал его. Ниже — текущее состояние.",
  },

  woAdjustments: {
    title: "Корректировки по этому заказу",
    loading: "Загрузка корректировок по этому заказу…",
    empty: "По этому заказу корректировок нет",
    error: "Не удалось загрузить корректировки по этому заказу.",
    requestId: "Идентификатор запроса",
    retry: "Повторить",
    more: "Показаны только последние {{count}} каждого вида. Остальные есть в журнале движений.",
    inLedger: "В журнале",
    openRequest: "Открыть заявку",
  },

  floor: {
    back: "Назад",

    sync: {
      online: "Связь есть",
      offline: "Нет связи",
      pending: "Ожидают отправки: {{count}}",
      allSynced: "Всё отправлено",
      attention: "Требуют внимания: {{count}}",
      retrying: "Пробуем снова — попытка {{attempts}}. Ваша работа сохранена.",
      signedOut:
        "Вы вышли из системы. Войдите снова, чтобы дослать свою работу — ничего не потеряно.",
      notDurable:
        "Это устройство не может сохранить очередь. Работа, сделанная без связи, пропадёт, если приложение закроется.",
      unreachable: "Система недоступна",
      sendNow: "Отправить сейчас",
      otherUser: "Часть записей сохранил другой человек: {{count}}. Пусть он войдёт и отправит их.",
      hydrateFailed:
        "Это устройство не смогло прочитать сохранённую на нём работу. Не выходите из системы — попробуйте через мгновение.",
      notSavedTitle: "Это НЕ сохранено",
      notSavedBody:
        "Устройство не смогло это записать, на отправку ничего не поставлено. Сделайте заново, а если не выходит — освободите место.",
    },

    cache: {
      showingSnapshot: "Показано то, что устройство загрузило в последний раз",
      ageJustNow: "только что",
      ageMinutes: "{{count}} мин назад",
      ageHours: "{{count}} ч назад",
      ageDays: "{{count}} дн. назад",
      needConnectionTitle: "На это устройство ещё не загружено",
      needConnectionBody:
        "Один раз поймайте связь, чтобы загрузить {{what}}. Дальше работает и без связи.",
      checking: "проверяем обновления",
    },

    principal: {
      signedIn: "Вы вошли: {{who}}",
      signInAgain: "Войдите снова, чтобы работа записалась на вас.",
    },

    home: {
      title: "Цех",
      scan: "Сканировать",
      scanSub: "Сначала ячейка, потом бобина — место обновится",
      stock: "Найти остаток",
      stockSub: "Поиск по продукту и длине",
      receive: "Принять перемещение",
      receiveSub: "Отметить, что приехало",
      counts: "Пересчёт",
      countsSub: "Пересчитать ячейку",
      tray: "Требует внимания",
      traySub: "Ничего не застряло",
      productCard: "Карта продукта",
      productCardSub: "Отсканируйте этикетку продукта, чтобы увидеть операции",
      unavailableUnknown: "Нужна связь один раз, чтобы планшет смог это открыть",
      unavailableChecking: "Проверяем, что здесь можно делать",
      unavailableWait: "Ещё проверяем. Откроется само.",
      unavailableRetryHint: "Нажмите, чтобы проверить ещё раз, когда появится связь",
      unavailableRetrying: "Проверяем ещё раз…",
    },

    op: {
      scan: "Сканирование · {{payload}}",
      cut: "Резка · отрезков: {{pieces}}",
      remeasure: "Перемер · {{measure}}",
      countRecord: "Пересчёт · {{value}}",
      countFound: "Пересчёт · найдена позиция",
      countSubmit: "Пересчёт · завершение",
      transferReceive: "Приёмка · строк: {{lines}}",
      opStarted: "Операция · старт",
      opPaused: "Операция · пауза",
      opResumed: "Операция · продолжение",
      opCompleted: "Операция · завершение",
      qtyReported: "Выработка · годных: {{good}}, брак: {{scrap}}",
      materialIssued: "Материал · {{qty}} {{uom}}",
      // D-081: отрицательное material_issued — это ВОЗВРАТ; лоток называет
      // его словом, а не знаком минус.
      materialReturned: "Возврат материала · {{qty}} {{uom}}",
      // Пять видов 04-C (task #96): раньше они падали в сырой запасной ключ.
      downtimeStarted: "Простой · начало",
      downtimeEnded: "Простой · конец",
      calibrationRecorded: "Замер барабана · заявлено {{claimed}}, измерено {{measured}} {{uom}}",
      // Общие формулировки направления по D-085, в однострочной грамматике
      // лотка.
      serialProduced: "Серийный № · сошло со станка, {{qty}} {{uom}}",
      serialConsumed: "Серийный № · пошло в станок, {{qty}} {{uom}}",
      defectFlagged: "Брак · отмечено {{qty}}",
      productionEvent: "Действие на участке · {{type}}",
      // Модуль 05: отходы и остатки в той же очереди (срез 05-B).
      wasteCapture: "Отходы · {{qty}} {{uom}}",
      remnantRegister: "Остаток · {{length}} м",
      // Модуль 07: контрольные замеры в той же очереди (срез 07-A).
      qualityResult: "Замер · {{value}}",
      qualityComplete: "Проверка · завершить",
    },

    scan: {
      title: "Сканирование",
      noPermission: "Ваша роль не может записывать сканирования. Обратитесь к кладовщику.",
      actionLegend: "Что вы делаете?",
      action: {
        locate: "Ставлю на место",
        lookup: "Просто смотрю",
        count: "Пересчитываю",
        receive: "Принимаю",
      },
      inputLabel: "Отсканируйте или введите этикетку",
      inputPlaceholder: "Наведите сканер или введите серийный номер",
      go: "Вперёд",
      cameraStart: "Включить камеру",
      cameraStop: "Выключить камеру",
      cameraUnavailable:
        "На этом устройстве нет сканера-камеры. Используйте сканер или введите серийный номер.",
      cameraDenied: "Камера не запустилась. Используйте сканер или введите серийный номер.",
      measureLabel: "Измеренная длина (необязательно)",
      measurePlaceholder: "например, 1543",
      measureHint: "Оставьте пустым, если не измеряли. Большая разница будет исправлена в журнале.",
      measureInvalid: "Введите число, не более шести знаков после запятой.",
      deviceUnstable:
        "Устройство не смогло сохранить свой идентификатор, поэтому после перезапуска сканирование ячейки и бобины может не связаться.",
      noStation:
        "Устройство не привязано к посту. Отсканируйте ячейку и нажмите “Считать это местом устройства”.",
      stationBound: "Устройство привязано к месту.",
      stationBind: "Считать это местом устройства",
      stationUnbind: "Отвязать",
      doneHere: "Здесь закончил",
      sessionWaiting: "Ожидает отправки",
      sessionNeedsYou: "Требует внимания",
      sending: "Отправляется…",
      queuedTitle: "Сохранено на устройстве",
      queuedOfflineBody: "Как только появится связь, это отправится и этикетка распознается.",
      queuedSlowBody: "Сеть медленная. Завершится в фоне.",
      failedTitle: "Это сканирование не принято",
      clear: "Очистить",
      unknownTitle: "Этикетка не распознана",
      unknownBody: "Записано, но эта этикетка не наша. Новые позиции заводит офис.",
      locationBody: "Ячейка записана. Теперь отсканируйте позицию — она встанет сюда.",
      unitTitleFallback: "Позиция",
      movedBody: "Теперь в {{path}}.",
      notMovedBody: "Записано. Место не изменилось.",
      offsiteBody:
        "Это в {{here}}, а по учёту — на другой площадке. Ничего не перемещено; офис уведомлён.",
      remeasureAdjusted: "Длина исправлена на {{delta}} — теперь {{remaining}}.",
      remeasureWithinTolerance: "Длина обновлена до {{remaining}} (в пределах допуска).",
      openUnit: "Открыть эту позицию",
    },

    stock: {
      title: "Найти остаток",
      searchLabel: "Что вы ищете?",
      searchPlaceholder: "например, 70mm 200m",
      pressGo: "Нажмите «Вперёд», чтобы искать.",
      offlineBody: "Для поиска нужна связь: остатки меняются каждую минуту.",
      promptTitle: "Найдите нужный остаток",
      promptBody: "Введите продукт и длину или отсканируйте этикетку.",
      errorTitle: "Не удалось выполнить поиск",
      noneTitle: "По запросу «{{query}}» ничего нет",
      noneBody: "Попробуйте без длины.",
      noneBroadBody: "Даже без длины ничего не находится.",
      showAnyway: "Искать без длины",
      broadenedNote: "Показаны результаты без запрошенной длины.",
      groupUnits: "Позиции",
      groupCombos: "Пары обрезков",
      groupBulk: "Навалом",
      groupCapped: "Показаны не все совпадения — уточните запрос.",
      productsCapped:
        "Подходит слишком много продуктов — часть остатков не показана. Добавьте название продукта.",
      comboTotal: "Вместе: {{total}}",
      lastSeen: "Замечена {{hours}} ч назад",
      neverScanned: "Ни разу не сканировалась",
      reserved: "В резерве · {{ref}}",
      open: "Открыть",
      degraded: "Подсказка по цене обрезков не показывается.",
    },

    unit: {
      title: "Позиция",
      thisUnit: "эту позицию",
      modeLabel: "Что сделать с этой позицией",
      notFoundTitle: "Не найдено",
      notFoundBody: "Такой позиции нет, либо она вам не видна.",
      ofCapacity: "из {{capacity}} {{uom}}",
      noCapacity: "Вместимость не указана",
      offcut: "Обрезок",
      noCutPermission: "Ваша роль не может резать. Обратитесь к кладовщику.",
    },

    cut: {
      title: "Резка",
      pieceN: "Длина отрезка {{n}}",
      piecePlaceholder: "например, 200",
      pieceKind: "Тип отрезка",
      kindChild: "Оставить как новый отрезок",
      kindWaste: "Отход (обрезь)",
      addPiece: "Добавить ещё отрезок",
      kerf: "Ширина реза ({{uom}}, необязательно)",
      kerfHint: "Считается один раз за рез кабеля, а не за каждый отрезок.",
      summary: "Запланировано {{planned}} из {{remaining}} {{uom}}",
      overRemaining: "Это больше, чем есть на позиции. В пределах допуска это всё же может пройти.",
      malformed: "Каждая длина — число, не более шести знаков после запятой.",
      noPieces: "Добавьте хотя бы один отрезок.",
      staleBaseline:
        "Эта длина — то, что устройство загрузило в последний раз. Проверьте бобину перед резкой.",
      replan: "Бобина изменилась — осталось {{remaining}} {{uom}}. Введите рез заново.",
      submit: "Отрезать",
      submitWithPlan: "Отрезать {{pieces}} шт. · {{planned}} из {{remaining}} {{uom}}",
      planAnother: "Запланировать ещё рез",
      submitting: "Идёт резка…",
      queued: "Сохранено на устройстве. Рез пройдёт в системе, как только появится связь.",
      failedTitle: "Этот рез не принят",
      doneTitle: "Рез выполнен",
      parentNow: "На этой позиции теперь {{remaining}} {{uom}}.",
      reservationNotCovered:
        "Резерв с этой позиции не удалось перенести ни на один новый отрезок — нужен кладовщик.",
    },

    remeasure: {
      title: "Перемер",
      noPermission: "Вы не можете перемерять позиции.",
      label: "Измеренная длина ({{uom}})",
      submit: "Сохранить измерение",
      queued: "Сохранено на устройстве. Отправится, как только появится связь.",
      failedTitle: "Это измерение не принято",
      adjusted: "Исправлено на {{delta}} — теперь {{remaining}}.",
      withinTolerance: "Обновлено до {{remaining}} (в пределах допуска, журнал не менялся).",
    },

    counts: {
      title: "Пересчёты",
      emptyTitle: "Пересчитывать нечего",
      emptyBody: "Для вас пересчёт пока не открывали. Его открывает кладовщик.",
      emptyOfflineBody:
        "На устройстве нет сохранённых пересчётов. Один раз поймайте связь, чтобы их загрузить.",
      thisList: "ваши пересчёты",
      loadFailedTitle: "Не удалось загрузить ваши пересчёты",
      loadFailedBody: "Это не то же самое, что «пересчётов нет». Попробуйте ещё раз.",
      blind: "Слепой пересчёт",
      open: "Пересчитать",
    },

    count: {
      title: "Пересчёт",
      thisWorksheet: "этот пересчёт",
      loadFailedTitle: "Не удалось загрузить этот пересчёт",
      loadFailedBody: "Попробуйте ещё раз или спросите кладовщика, открыт ли он.",
      blindBadge: "Слепой пересчёт",
      blindNote: "Считайте то, что видите. Ожидаемые числа скрыты намеренно.",
      openNote: "Вводите то, что насчитали в каждой ячейке.",
      progress: "Пересчитано {{counted}} из {{total}}",
      notCounting: "Пересчёт в статусе «{{status}}» и записи больше не принимает.",
      noPermission: "Ваша роль не может записывать пересчёт. Обратитесь к кладовщику.",
      binHead: "Ячейки",
      emptyTitle: "В этом пересчёте нет строк",
      emptyBody: "На момент запуска в охват ничего не попало.",
      measureLabel: "Измерено ({{uom}})",
      qtyLabel: "Насчитано ({{uom}})",
      noteLabel: "Примечание",
      notePlaceholder: "Необязательно",
      skipHint: "Примечание без числа помечает строку как пропущенную.",
      record: "Сохранить строку",
      recorded: "Сохранить снова",
      recordSaving: "Сохраняется…",
      saved: "Сохранено на устройстве.",
      lineParked: "Эта строка отклонена и ждёт вас.",
      openTray: "Открыть то, что требует внимания",
      binScanLabel: "Отсканируйте ячейку, где вы находитесь",
      binScanPlaceholder: "Наведите сканер на этикетку стеллажа",
      binNoMatch: "“{{payload}}” — не ячейка из этого пересчёта. Выберите её из списка.",
      handOff: "Пересчёт закончен. Закрывает его кладовщик — передайте устройство или скажите ему.",
      foundHead: "Нашли то, чего нет в списке",
      foundHint: "Отсканируйте — и это добавится в пересчёт.",
      foundNeedsBin: "Сначала выберите ячейку, в которой вы стоите.",
      foundLabel: "Отсканируйте лишнюю позицию",
      foundPlaceholder: "Наведите на неё сканер",
      foundQueued: "Сохранено. Позиция добавится в пересчёт, как только этикетка распознается.",
      foundNotSaved: "Это не сохранилось на устройстве. Попробуйте ещё раз.",
      submit: "Завершить пересчёт",
      submitBlocked: "Осталось пересчитать строк: {{remaining}}",
      submitQueued: "Сохранено на устройстве. Пересчёт закроется, как только появится связь.",
      submitFailedTitle: "Пересчёт закрыть не удалось",
      submitted: "Пересчитано и отправлено на проверку.",
    },

    receive: {
      listTitle: "Принять перемещение",
      staysInTransit: "Это перемещение не принимаем — остаётся в пути.",
      progress: "Готово {{done}} строк из {{total}}",
      scanTicked: "✓ {{name}} отмечено · {{done}} из {{total}}",
      blockedReason: "Для каждой недостачи нужна причина",
      blockedOver: "В одной строке больше, чем отправляли",
      blockedMalformed: "Проверьте числа",
      title: "Приёмка",
      thisManifest: "это перемещение",
      emptyTitle: "В пути ничего нет",
      emptyBody: "Вам ничего не отправляли.",
      emptyOfflineBody:
        "На устройстве нет сохранённых перемещений. Один раз поймайте связь, чтобы их загрузить.",
      thisInbound: "то, что едет к вам",
      listLoadFailedTitle: "Не удалось загрузить то, что едет к вам",
      listLoadFailedBody: "Это не то же самое, что «ничего не едет». Попробуйте ещё раз.",
      noCarrier: "Перевозчик не указан",
      open: "Открыть",
      loadFailedTitle: "Не удалось загрузить это перемещение",
      loadFailedBody: "Попробуйте ещё раз или сверьтесь с бумажной накладной.",
      noPermission: "Ваша роль не может принимать перемещения. Обратитесь к кладовщику.",
      wrongState: "Перемещение в статусе «{{status}}» и приёмку не принимает.",
      noLinesTitle: "В этом перемещении ничего нет",
      noLinesBody: "Его отправили без строк. Свяжитесь с отправляющей площадкой.",
      unnamedLine: "Строка {{n}}",
      dispatched: "Отправлено: {{qty}} {{uom}}",
      tickWhole: "Пришло полностью",
      qtyLabel: "Пришло ({{uom}})",
      overReceive:
        "Это больше, чем отправляли. Примите отправленное; лишнее найденное оформляется отдельной корректировкой.",
      reasonLabel: "Почему недостача?",
      reasonPick: "Выберите причину…",
      reason: {
        spillage: "Просыпано или пролито",
        damage: "Повреждено в пути",
        short_shipped: "Недогруз",
        miscount: "Просчёт при отправке",
        other: "Другое",
      },
      shortfallNote: "Недостающее остаётся в пути, пока его не найдут или не спишут.",
      needsReason: "Для каждой недостачи нужна причина, иначе подтвердить нельзя.",
      scanHead: "Отмечайте позиции сканированием",
      scanLabel: "Отсканируйте позицию из этого перемещения",
      scanPlaceholder: "Наведите на неё сканер",
      scanNoMatch: "«{{payload}}» в этом перемещении нет.",
      confirm: "Подтвердить строк: {{count}}",
      queued: "Сохранено на устройстве. Приёмка пройдёт, как только появится связь.",
      failedTitle: "Эта приёмка не принята",
      doneComplete: "Принято полностью.",
      donePartial: "Принято. Часть строк осталась незакрытой.",
    },

    tray: {
      title: "Требует внимания",
      pendingNote: "Ещё ожидают отправки: {{count}}.",
      pendingHead: "Ожидают отправки",
      pendingBody: "Сохранено на устройстве. Уйдёт, когда появится связь; повторять не нужно.",
      pendingBlocked: "Это первое в очереди, отправка повторяется.",
      emptyTitle: "Ничего не застряло",
      emptyBody: "Всё, что вы сделали, отправлено.",
      emptyBodyPending: "Ничего не застряло — остальное ещё в пути.",
      unknownReason: "Это не удалось отправить, и причина не записана.",
      networkFailure: "Устройство не смогло связаться с системой.",
      forbidden: "Вашей роли это не разрешено.",
      notFound: "То, к чему это относится, больше не существует.",
      freshRemaining: "На позиции {{serial}} теперь {{remaining}}.",
      availableQty: "На самом деле доступно только {{available}}.",
      attempts: "Попыток: {{count}}",
      adviceFutile: "Повторять бесполезно — это уже использовано для другого.",
      adviceReplan:
        "Пока это ждало, всё изменилось. Откройте и сделайте заново с тем, что есть сейчас.",
      retry: "Попробовать снова",
      open: "Открыть",
      discard: "Выбросить",
      discardConfirm: "Да, выбросить",
      discardWarning:
        "Это удалит то, что ждёт на устройстве. То, что система уже сделала, не отменяется.",
      supportDetails: "Данные для поддержки",
      otherUserHead: "Сохранил другой человек",
      otherUserBody:
        "На этом устройстве часть записей сохранил другой человек: {{count}}. Под вашим именем они не отправятся. Попросите его войти.",
      reviewHead: "Требует разбора в офисе",
      reviewBody:
        "Офис получил эти действия, но не смог их применить. Руководитель разберётся; ничего не потеряно.",
      reviewDismiss: "Убрать с устройства",
      reviewDismissConfirm: "Да, убрать",
      reviewDismissWarning:
        "Это убирает только запись на этом устройстве. Она уже передана в офис, где её разберёт руководитель.",
      reviewReasonFallback: "Не удалось применить ({{reason}}).",
      reviewReasons: {
        invalid_transition: "К моменту получения операция была уже в другом состоянии.",
        qty_exceeds_remaining: "Указанное количество больше, чем оставалось по операции.",
        op_already_completed: "Операция уже была завершена.",
        wo_cancelled: "Заказ-наряд был отменён.",
        stock_insufficient: "По учёту не хватило запаса этого материала.",
        unknown_reference: "То, к чему это относится, не найдено.",
        stale_snapshot:
          "Время на этом устройстве выглядит неверным. Офис проверит, когда это произошло на самом деле.",
        qc_hold: "Операция приостановлена контролем качества.",
        worker_not_authenticated: "Записано не тем работником, который вошёл в систему.",
        unsupported_event_kind: "Система пока не поддерживает это действие.",
        result_missing: "В ответе системы это действие не упомянуто.",
        result_unrecognized: "Ответ системы по этому действию не удалось распознать.",
      },
    },
  },
};
